import { useState, useRef } from 'react'
import { Draggable } from 'react-smooth-dnd'
import Add from '@mui/icons-material/Add'
import Check from '@mui/icons-material/Check'
import ContentCopy from '@mui/icons-material/ContentCopy'
import Delete from '@mui/icons-material/Delete'
import DragIndicator from '@mui/icons-material/DragIndicator'
import Edit from '@mui/icons-material/Edit'
import MoreVert from '@mui/icons-material/MoreVert'
import PanTool from '@mui/icons-material/PanTool'
import Pause from '@mui/icons-material/Pause'
import PlayDisabled from '@mui/icons-material/PlayDisabled'
import TrendingFlat from '@mui/icons-material/TrendingFlat'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  Stack,
  Paper,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  Tooltip,
  Button,
  useTheme,
} from '@mui/material'
import { indigo } from '@mui/material/colors'
import { QueryKey } from '@tanstack/react-query'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useOverlay from '@/hooks/useOverlay'
import useProjectFeatureEnabled from '@/hooks/useProjectFeatureEnabled'
import { useGetWorkflowStateActionTypes } from '@/service-library/hooks/workflow-state-action-types'
import {
  useDeleteWorkflowStateEvent,
  useUpdateWorkflowStateEvent,
} from '@/service-library/hooks/workflow-state-events'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { SUPER_USER_ONLY_COLOR } from '@/theme/usePixydocsTheme'
import {
  WorkflowStateAction,
  WorkflowStateActionType,
} from '@/types/workflow-state-actions'
import { WorkflowStateEvent } from '@/types/workflow-state-events'
import { updateParams } from '@/utils/rules'
import { showErrorSnackbar, showInfoSnackbar } from '@/utils/snackbars'
import generateUuid from '@/utils/uuid'
import { isActionTypeFlagEnabled } from '@/utils/workflow-state-action-types'
import {
  getSystemEvent,
  ON_ENTER_EVENT_CODE,
} from '@/utils/workflow-state-events'
import copyToClipboard from '@/components/document-actions-menu/copy-to-clipboard'
import { DraggableList } from '@/components/lists/DraggableList'
import AddEditWorkflowStateEventDialog from '@/components/workflow-state-page/AddEditWorkflowStateEventDialog'
import { useWorkflowStatePageContext } from '@/components/workflow-state-page/WorkflowStatePage'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import ActionCard from './ActionCard'
import DeleteWorkflowStateEventDialog from './DeleteWorkflowStateEventDialog'

const whenDocHasErrorsMap = {
  trigger: {
    message: 'Fire Without Warning',
    icon: Check,
  },
  trigger_with_warning: {
    message: 'Warn Before Firing',
    icon: Pause,
  },
  do_not_trigger: {
    message: 'Requires Clean Document',
    icon: PlayDisabled,
  },
}

type EventLaneProps = {
  workflowStateEvent: WorkflowStateEvent
  listQueryKey: QueryKey
  isDraggable?: boolean
  isPrimaryEvent?: boolean
}

export default function EventLane({
  workflowStateEvent,
  listQueryKey,
  isDraggable = false,
  isPrimaryEvent = false,
}: EventLaneProps) {
  const editOverlay = useOverlay()
  const laneMenuOverlay = useOverlay()
  const deleteOverlay = useOverlay()
  const newActionOverlay = useOverlay()
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const newActionMenuAnchorRef = useRef<HTMLButtonElement>(null)
  const { workflowState } = useWorkflowStatePageContext()
  const { selectedWorkflow } = useSelectedWorkflowContext()
  const featureFlags = useFeatureFlagContext()
  const isSuperUser = useIsSuperUser()
  const { isEnabled: isPurchaseOrdersEnabled } =
    useProjectFeatureEnabled('purchase-orders')

  const systemEvent = getSystemEvent(workflowStateEvent.code)

  const isInitialStateOnEnter =
    workflowStateEvent.code === ON_ENTER_EVENT_CODE &&
    selectedWorkflow?.initial_workflow_state_id === workflowState.id

  const [actionsBeingEdited, setActionsBeingEdited] = useState<
    Record<string, boolean>
  >({})

  const { workflowStateActionTypes } = useGetWorkflowStateActionTypes({
    filters: { limit: '1000' },
  })

  const { updateWorkflowStateEvent } = useUpdateWorkflowStateEvent({
    listQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to update event. Please try again later.')
    },
  })

  const { deleteWorkflowStateEvent } = useDeleteWorkflowStateEvent({
    listQueryKey,
    onError: () => {
      showErrorSnackbar('Failed to delete event. Please try again later.')
    },
  })

  // We use state here to prevent flickering when drag-and-dropping to change sort order
  const [actions, setActions] = useState<WorkflowStateAction[]>(
    workflowStateEvent.actions || [],
  )

  function onReorder(updatedWorkflowStateActions: WorkflowStateAction[]) {
    const updatedOrder = updatedWorkflowStateActions.map((state, index) => ({
      ...state,
      sort_order: index,
    }))
    setActions(updatedOrder)
    updateWorkflowStateEvent({
      ...workflowStateEvent,
      actions: updatedOrder,
    })
  }

  function onDeleteAction(action: WorkflowStateAction) {
    const updatedActions = actions.filter((a) => a.id !== action.id)
    setActions(updatedActions)
    updateWorkflowStateEvent({
      ...workflowStateEvent,
      actions: updatedActions,
    })
  }

  function onSaveAction(action: WorkflowStateAction) {
    const updatedActions = actions.map((a) =>
      a.id === action.id
        ? action
        : {
            ...a,
            // using this to clear old data that saved empty strings for uuid fields
            params: updateParams(
              a.params,
              a.action_type?.params_schema?.properties,
            ),
          },
    )

    setActions(updatedActions)
    updateWorkflowStateEvent({
      ...workflowStateEvent,
      actions: updatedActions,
    })
  }

  function onAddAction(actionType: WorkflowStateActionType) {
    const actionId = generateUuid()

    setActions((prev = []) => [
      ...prev,
      {
        id: actionId,
        name: actionType.name,
        sort_order: prev.length + 1000000,
        event_id: workflowStateEvent?.id || '',
        action_type_id: actionType.id,
        params: {},
        action_type: actionType,
        is_enabled: true,
      },
    ])
    newActionOverlay.close()

    // Set isEditing to true so that the action card is in edit mode when it shows up
    setActionsBeingEdited((prev) => ({
      ...prev,
      [actionId]: true,
    }))
  }

  // If this is a new, unsaved action, remove it from the list of actions
  function onCancelAction(action: WorkflowStateAction) {
    if (!workflowStateEvent.actions?.find((a) => a.id === action.id)) {
      setActions((prev) => prev.filter((a) => a.id !== action.id))
    }
  }

  const theme = useTheme()

  const Icon =
    whenDocHasErrorsMap[workflowStateEvent.when_document_has_errors].icon

  const orderedWorkflowStateActionTypes = workflowStateActionTypes.sort(
    (a, b) => {
      if (a.is_superuser !== b.is_superuser) {
        return a.is_superuser ? 1 : -1
      }
      return a.name.localeCompare(b.name)
    },
  )

  return (
    <Stack direction="row" component={Paper} square>
      <Paper
        sx={{
          width: 300,
          maxWidth: 300,
          minWidth: 300,
          p: 2,
          position: 'sticky',
          left: 0,
          zIndex: 2,
          borderRight:
            theme.palette.mode === 'light'
              ? `1px solid ${theme.palette.divider}`
              : 'none',
        }}
        elevation={0}
        square
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {isDraggable && (
            <DragIndicator
              className="event-drag-handle"
              sx={{
                cursor: 'grab',
                ':active': { cursor: 'grabbing' },
              }}
            />
          )}
          <Typography
            variant="body1"
            sx={{ flexGrow: 1, pl: isDraggable ? 0 : '6px' }}
          >
            {workflowStateEvent.name}
          </Typography>

          <IconButton
            size="small"
            ref={menuButtonRef}
            onClick={laneMenuOverlay.open}
          >
            <MoreVert />
          </IconButton>

          {/* MARK: Lane Menu */}
          <Menu
            open={laneMenuOverlay.isOpen}
            anchorEl={menuButtonRef.current}
            onClose={laneMenuOverlay.close}
          >
            {!systemEvent && (
              <MenuItem
                onClick={() => {
                  laneMenuOverlay.close()
                  editOverlay.open()
                }}
              >
                <ListItemIcon>
                  <Edit fontSize="small" />
                </ListItemIcon>
                Edit
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                copyToClipboard(workflowStateEvent.id)
                showInfoSnackbar('Copied to Clipboard')
                laneMenuOverlay.close()
              }}
            >
              <ListItemIcon>
                <ContentCopy fontSize="small" />
              </ListItemIcon>
              Copy ID
            </MenuItem>
            <MenuItem
              onClick={() => {
                laneMenuOverlay.close()
                deleteOverlay.open()
              }}
            >
              <ListItemIcon>
                <Delete fontSize="small" />
              </ListItemIcon>
              Delete
            </MenuItem>
          </Menu>

          {/* MARK: Edit Dialog */}
          <AddEditWorkflowStateEventDialog
            overlay={editOverlay}
            workflowState={workflowState}
            workflowStateEvent={workflowStateEvent}
            listQueryKey={listQueryKey}
          />

          {/* MARK: Delete Dialog */}
          <DeleteWorkflowStateEventDialog
            overlay={deleteOverlay}
            workflowStateEvent={workflowStateEvent}
            isInitialStateOnEnter={isInitialStateOnEnter}
            onDelete={() => {
              deleteWorkflowStateEvent(workflowStateEvent.id)
              deleteOverlay.close()
            }}
          />
        </Stack>

        {systemEvent && (
          <Box sx={{ ml: '6px', mt: 1 }}>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary' }}
            >
              {systemEvent.description}
            </Typography>
          </Box>
        )}

        {!systemEvent && (
          <Box sx={{ ml: '6px', mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {workflowStateEvent.move_to_next_doc_in_validation ? (
                <TrendingFlat sx={{ fontSize: 14, color: 'text.secondary' }} />
              ) : (
                <PanTool sx={{ fontSize: 14, color: 'text.secondary' }} />
              )}

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  transform: 'translateY(2px)',
                }}
              >
                {workflowStateEvent.move_to_next_doc_in_validation
                  ? 'Move to Next'
                  : 'Stay on Document'}
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Icon sx={{ fontSize: 14, color: 'text.secondary' }} />

              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  color: 'text.secondary',
                  transform: 'translateY(2px)',
                }}
              >
                {
                  whenDocHasErrorsMap[
                    workflowStateEvent.when_document_has_errors
                  ].message
                }
              </Typography>
            </Stack>

            {isPrimaryEvent && (
              <Tooltip title="This event will be shown as the primary option wherever these events can be triggered.">
                <Typography
                  variant="caption"
                  sx={{
                    ml: '2px',
                    display: 'block',
                    transform: 'translateY(2px)',
                    color: indigo['A200'],
                  }}
                >
                  Primary Event
                </Typography>
              </Tooltip>
            )}

            {!workflowStateEvent.allow_manual_trigger && (
              <Stack direction="row" alignItems="center" spacing={1}>
                <VisibilityOff sx={{ fontSize: 14, color: 'text.secondary' }} />

                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    transform: 'translateY(2px)',
                  }}
                >
                  Cannot Trigger Manually
                </Typography>
              </Stack>
            )}
          </Box>
        )}
      </Paper>
      {/* MARK: Action Cards */}
      <Stack
        direction="row"
        spacing={1}
        sx={{ py: 2, px: 1, flexGrow: 1, overflow: 'auto' }}
      >
        {actions.length > 0 && (
          <DraggableList
            orientation="horizontal"
            lockAxis="x"
            onChange={onReorder}
            data={actions}
          >
            {actions?.map((action) => {
              if (
                !featureFlags.show_action_python_script &&
                action.action_type?.code === 'custom_python_script'
              )
                return null

              return (
                // @ts-expect-error -- Draggable can have children
                <Draggable key={action.id}>
                  <ActionCard
                    action={action}
                    isEditing={actionsBeingEdited[action.id]}
                    setIsEditing={(isEditing) => {
                      setActionsBeingEdited((prev) => ({
                        ...prev,
                        [action.id]: isEditing,
                      }))
                    }}
                    onSave={onSaveAction}
                    onDelete={onDeleteAction}
                    onCancel={onCancelAction}
                  />
                </Draggable>
              )
            })}
          </DraggableList>
        )}

        <Box>
          <Button
            variant="text"
            startIcon={<Add />}
            sx={{ mt: '1px', flexShrink: 0, textWrap: 'nowrap' }}
            onClick={newActionOverlay.open}
            ref={newActionMenuAnchorRef}
          >
            Add Action
          </Button>

          <Menu
            anchorEl={newActionMenuAnchorRef.current}
            open={newActionOverlay.isOpen}
            onClose={newActionOverlay.close}
          >
            {orderedWorkflowStateActionTypes.map((actionType, index) => {
              if (
                actionType.code === 'export_to_csv' ||
                actionType.code === 'export_to_json' ||
                (!featureFlags.show_action_python_script &&
                  actionType.code === 'custom_python_script') ||
                (!featureFlags.use_new_run_prediction_model &&
                  actionType.code === 'run_prediction_linked_model') ||
                (featureFlags.use_new_run_prediction_model &&
                  actionType.code === 'run_prediction_model') ||
                (actionType.is_superuser && !isSuperUser) ||
                (actionType.code === 'llm_purchase_order_match' &&
                  !isPurchaseOrdersEnabled) ||
                !isActionTypeFlagEnabled(actionType, featureFlags)
              )
                return null
              return (
                <MenuItem
                  key={index}
                  onClick={() => {
                    onAddAction(actionType)
                  }}
                  sx={{
                    color: actionType.is_superuser
                      ? SUPER_USER_ONLY_COLOR
                      : undefined,
                  }}
                >
                  {actionType.name}
                </MenuItem>
              )
            })}
          </Menu>

          <Box sx={{ width: 300 }} />
        </Box>
      </Stack>
    </Stack>
  )
}
